import { type Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useImperativeHandle } from 'react';

import Combobox from 'components/helpers/Combobox/Combobox';
import Listbox from 'components/helpers/Listbox/Listbox';
import Option from 'components/helpers/Option/Option';
import useFocusController from 'lib/hooks/useFocusController';
import Key from 'lib/key';
import { isAlphanumeric } from 'lib/regex';

import styles from './Select.module.scss';

/** @todo abstract <Label> into helper component */
type LabelPosition = 'left' | 'top';

/** Properties interface of an individual select option. */
export interface SelectOption {
  /**
   * Unique identifier of the select option.
   * Uses `value` if not provided.
   */
  id?: string;

  /**
   * Sitecore label field for the select option.
   * Uses `value` text if not provided.
   */
  label?: Field<string>;

  /** Value of the select option. */
  value: string;
}

/** Properties interface of the select. */
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Place a checkmark next to selected options in dropdown. */
  checkSelectedOption?: boolean;

  /**
   * Indicates that the select is disabled.
   * @defaultValue `false`
   */
  disabled?: boolean;

  /** Unique identifier of the select. */
  id: string;

  /**
   * Label of the select.
   * @defaultValue `'Select an option'`
   */
  label: string;

  /**
   * Position of the label.
   * @defaultValue `'top'`
   */
  labelPosition?: LabelPosition;

  /**
   * Callback method for when the select value changes.
   * @param e - A change event.
   */
  onChange?: React.ChangeEventHandler;

  /**
   * Array of options to display within the listbox.
   * @defaultValue `[]`
   */
  options?: SelectOption[];

  /**
   * Placeholder text displayed when the combobox has no value.
   * @defaultValue `'Select...'`
   */
  placeholder?: string;

  /**
   * Value to be submitted to a form.
   * @defaultValue `''`
   */
  value?: string;
}

/** Default properties of the select. */
const defaultProps: Partial<SelectProps> = {
  disabled: false,
  label: 'Select an option',
  labelPosition: 'top',
  options: [],
  placeholder: 'Select...',
};

/**
 * Classnames binding for the select.
 * @see [Classnames Usage](https://github.com/JedWatson/classnames#alternate-bind-version-for-css-modules)
 */
const cx = classNames.bind(styles);

export type SelectHandle = {
  focus: () => void;
  blur: () => void;
};

/**
 * A Sitecore-ready implementation of the select.
 *
 * @see [Orchid Storybook](https://upmchporchid.azurewebsites.net/storybook/?path=/story/components-select--default) for design references
 * @see [Orchid Design System](https://upmchporchid.azurewebsites.net/components/single-select-dropdown) for usage details
 *
 * @example
 * ```tsx
 * <Select label="Choose a fruit" options={['Apples', 'Bananas', 'Pears']} value="Pears" />
 * ```
 *
 * @param props - Select properties.
 * @returns A JSX element.
 */
const Select = React.forwardRef<SelectHandle, SelectProps>((propsWithoutDefaults, ref) => {
  const props = {
    labelPosition: propsWithoutDefaults.labelPosition ?? defaultProps.labelPosition,
    options: propsWithoutDefaults.options ?? defaultProps.options,
    placeholder: propsWithoutDefaults.placeholder ?? defaultProps.placeholder,
    ...propsWithoutDefaults,
  };

  const { className, checkSelectedOption, disabled, id, label, labelPosition, onChange, options, placeholder } = props;
  const { sitecoreContext } = useSitecoreContext();

  /** Expanded state of the select. */
  const [expanded, setExpanded] = React.useState<boolean>(false);
  /** Internal value of the select. */
  const [selectedValue, setSelectedValue] = React.useState<string>('');

  /** Hook used to control focus across a group of elements. */
  const focusController = useFocusController<HTMLLIElement>({ behavior: 'aria-activedescendant' });
  /** The currently focused option. */
  const focusedOption = focusController.getFocusedElement();
  /** The currently selected option. */
  const selectedOption = focusController.getElementRefByAttribute('aria-selected', 'true');

  /** Unique identifier of the combobox. */
  const comboboxId = `${id}-combobox`;
  /** Unique identifier of the listbox. */
  const listboxId = `${id}-listbox`;

  /** Element reference for the combobox. */
  const comboboxRef = React.useRef<HTMLButtonElement>(null);

  /** Alphanumeric keys pressed within a specified timeout window. */
  const alphanumericKeysPressed = React.useRef<string>('');
  /** Timeout for when the alphanumeric key press window closes. */
  const alphanumericKeyTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Clears all stored alphanumeric keys and timeouts. */
  const clearAlphanumericKeys = (): void => {
    if (alphanumericKeysPressed && alphanumericKeysPressed.current) {
      alphanumericKeysPressed.current = '';
    }

    if (alphanumericKeyTimeout && alphanumericKeyTimeout.current) {
      alphanumericKeyTimeout.current = null;
    }
  };

  /**
   * Provides the value of the select.
   * @returns A string.
   */
  const getValue = (): string => {
    const { value } = props;
    return value === '' ? value : value || selectedValue;
  };

  /**
   * Helper method to handle keyboard functionality when an alphanumeric key is pressed.
   * @param key - A string.
   * @param cb - A callback method.
   */
  const handleAlphanumericKeyDown = (key: string, cb: () => void): void => {
    const delay = 500;

    if (!alphanumericKeyTimeout.current) {
      alphanumericKeyTimeout.current = setTimeout(cb, delay);
    }

    alphanumericKeysPressed.current += key;
  };

  /** Event handler for when the combobox is blurred. */
  const handleBlur: React.FocusEventHandler<HTMLButtonElement> = (e) => {
    if (expanded && e.relatedTarget?.role !== 'option') {
      setExpanded(false);
    }
  };

  /**
   * Event handler for when the combobox is clicked.
   * @param e - A mouse event.
   */
  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    setExpanded(!expanded);

    if (expanded && comboboxRef && comboboxRef.current) {
      comboboxRef.current?.focus();
    }
  };

  /**
   * Helper method to handle keyboard functionality while the select is collapsed.
   * @param e - A keyboard event.
   */
  const handleKeyDownWhileCollapsed: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    const { key } = e;

    let preventDefault = true;

    switch (key) {
      case Key.ArrowDown:
      case Key.Enter:
      case Key.Space:
        setExpanded(true);
        if (selectedOption) {
          focusController.focusElementByReference(selectedOption);
        } else {
          focusController.focusFirstElement();
        }
        break;
      case Key.ArrowUp:
      case Key.Home:
        setExpanded(true);
        focusController.focusFirstElement();
        break;
      case Key.End:
        setExpanded(true);
        focusController.focusLastElement();
        break;
      case Key.Escape:
        if (comboboxRef && comboboxRef.current) {
          comboboxRef.current?.blur();
        }
        break;
      default:
        if (isAlphanumeric(key)) {
          handleAlphanumericKeyDown(key, setSelectedOptionByAlphanumericKeys);
          preventDefault = true;
        } else {
          clearAlphanumericKeys();
          preventDefault = false;
        }
        break;
    }

    if (preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /**
   * Helper method to handle keyboard functionality while the select is expanded.
   * @param key - A key.
   */
  const handleKeyDownWhileExpanded: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    const { key } = e;

    let preventDefault = true;

    switch (key) {
      case Key.ArrowDown:
        focusController.focusNextElement();
        break;
      case Key.ArrowUp:
        focusController.focusPreviousElement();
        break;
      case Key.PageDown:
        focusController.focusElementByIncrement(10);
        break;
      case Key.PageUp:
        focusController.focusElementByIncrement(-10);
        break;
      case Key.Home:
        focusController.focusFirstElement();
        break;
      case Key.End:
        focusController.focusLastElement();
        break;
      case Key.Enter:
      case Key.Space:
        if (focusedOption && focusedOption.dataset.value && selectedOption !== focusedOption) {
          setSelectedOptionByValue(focusedOption.dataset.value, true);
        }
        setExpanded(false);
        break;
      case Key.Escape:
        setExpanded(false);
        break;
      default:
        if (isAlphanumeric(key)) {
          handleAlphanumericKeyDown(key, setFocusedOptionByAlphanumericKeys);
          preventDefault = true;
        } else {
          clearAlphanumericKeys();
          preventDefault = false;
        }
        break;
    }

    if (preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  /**
   * Event handler for when a key is pressed while the combobox is focused.
   * @param e - A keyboard event.
   */
  const handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (expanded) {
      handleKeyDownWhileExpanded(e);
    } else {
      handleKeyDownWhileCollapsed(e);
    }
  };

  /**
   * Event handler for when the mouse is held down on the combobox.
   * @param e - A mouse event.
   */
  const handleMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
  };

  /**
   * Event handler for when an option is clicked.
   * @param e - A mouse event.
   */
  const handleOptionClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
    const { currentTarget } = e;

    e.preventDefault();
    e.stopPropagation();

    if (currentTarget && currentTarget.dataset.value) {
      setSelectedOptionByValue(currentTarget.dataset.value);
    }
  };

  /** Sets the focused option by alphanumeric keys pressed. */
  const setFocusedOptionByAlphanumericKeys = (): void => {
    let option: HTMLLIElement | null = null;

    if (alphanumericKeysPressed && alphanumericKeysPressed.current) {
      option = focusController.getElementRefByTextContent(alphanumericKeysPressed.current);
    }

    if (option) {
      focusController.focusElementByReference(option);
    }

    clearAlphanumericKeys();
  };

  /**
   * Sets the selected option given a value.
   * @param nextValue - A string.
   * @param nextExpanded - A boolean.
   */
  const setSelectedOptionByValue = (nextValue: string, nextExpanded = false): void => {
    if (nextValue !== selectedValue) {
      setSelectedValue(nextValue);
      triggerOnChange(nextValue);
    }

    if (nextExpanded !== expanded) {
      setExpanded(nextExpanded);
    }
  };

  /** Sets the selected option by alphanumeric keys pressed. */
  const setSelectedOptionByAlphanumericKeys = (): void => {
    let option: HTMLLIElement | null = null;

    if (alphanumericKeysPressed && alphanumericKeysPressed.current) {
      option = focusController.getElementRefByTextContent(alphanumericKeysPressed.current);
    }

    if (option && option.dataset && option.dataset.value) {
      setSelectedOptionByValue(option.dataset.value);
    }

    clearAlphanumericKeys();
  };

  /**
   * Triggers an onChange event given a provided value.
   * @param value - A string value.
   */
  const triggerOnChange = (value: string): void => {
    const event = { target: { value } };
    const changeEvent = { nativeEvent: event, ...event };

    if (onChange) {
      onChange(changeEvent as unknown as React.ChangeEvent);
    }
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        focus() {
          comboboxRef.current?.focus();
        },
        blur() {
          comboboxRef.current?.blur();
        },
      };
    },
    []
  );

  let comboboxValue = '';
  const value = getValue();

  const fieldLabel: Field<string> = { value: label || defaultProps.label || '' };
  const optionElements = options?.map((option: SelectOption, optionIndex: number) => {
    const optionValue = option?.value;
    const optionId = option?.id ?? optionValue;
    const optionLabel = option?.label ?? { value: optionValue };
    const isSelected = optionValue === value;

    if (isSelected) {
      comboboxValue = optionLabel?.value;
    }

    return (
      <Option
        ariaSelected={isSelected}
        checkSelected={checkSelectedOption}
        focused={optionId === focusedOption?.id}
        id={optionId}
        key={optionId}
        onClick={handleOptionClick}
        ref={(el) => focusController.addToElementRefs(el, optionIndex)}
        value={optionValue}
      >
        <Text encode={sitecoreContext.pageEditing} field={optionLabel} />
      </Option>
    );
  });

  return (
    <div className={cx({ select: true, [labelPosition as LabelPosition]: true }, className)}>
      <label className={cx({ label: true })} htmlFor={comboboxId}>
        <Text field={fieldLabel} encode={sitecoreContext.pageEditing} />
      </label>
      <div className={cx({ container: true })}>
        <Combobox
          ariaActiveDescendant={focusedOption?.id}
          ariaControls={listboxId}
          ariaExpanded={expanded}
          ariaHasPopup="listbox"
          ariaOwns={listboxId}
          className={cx({ combobox: true })}
          disabled={disabled}
          icon="carat"
          id={comboboxId}
          onBlur={handleBlur}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          placeholder={placeholder}
          ref={comboboxRef}
          value={comboboxValue}
        />
        <Listbox ariaLabelledBy={comboboxId} hidden={!expanded} id={listboxId}>
          {optionElements}
        </Listbox>
      </div>
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
